import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { ShopAttributes } from '@/components/models.ts';

type KlaviyoLearnQ = unknown[] & {
  isIdentified?: () => boolean;
  push: (...events: unknown[]) => number;
};

declare global {
  interface Window {
    _learnq?: KlaviyoLearnQ;
  }
}

export const useSystemStore = defineStore('systemStore', () => {
  const shopAttributes = ref<ShopAttributes>({
    domain: '',
    region: '',
    regionWithDev: '',
    currency: {
      code: 'US',
      symbol: '$',
      fullCode: 'USD',
    },
    themeId: 0,
    klaviyoList: '',
    paymentProvider: '',
    paymentProviderSplit: 0,
  });

  const setShopAttributes = (shopObject: ShopAttributes) => {
    shopAttributes.value = shopObject;
  };
  const currency = computed(() => shopAttributes.value.currency);
  const region = computed(() => shopAttributes.value.region.toUpperCase());
  const klaviyoList = computed(() => shopAttributes.value.klaviyoList);
  const paymentProvider = computed(() => shopAttributes.value.paymentProvider);
  const paymentProviderSplit = computed(() => shopAttributes.value.paymentProviderSplit);
  const frontEndApi = computed(() =>
    ['INT', 'EU', 'CA', 'UK'].includes(region.value)
      ? 'https://webapi.hismileteeth.com'
      : 'https://api.hismileteeth.com',
  );
  const currentPath = ref(window.location.pathname);
  const userEmailAcquired = ref<boolean | null>(null);

  const setUserEmailAcquired = (value: boolean) => {
    userEmailAcquired.value = value;
    sessionStorage.setItem('user_email_acquired', JSON.stringify(value));
  };

  const resolveUserEmailAcquired = (limit = 5): boolean | null => {
    const storedValue = sessionStorage.getItem('user_email_acquired');
    if (storedValue !== null) {
      const parsedValue = JSON.parse(storedValue) as boolean;
      userEmailAcquired.value = parsedValue;
      return parsedValue;
    }
    const identified = window._learnq?.isIdentified?.();
    if (typeof identified === 'boolean') {
      setUserEmailAcquired(identified);
      return identified;
    }
    if (limit > 0) {
      window.setTimeout(() => resolveUserEmailAcquired(limit - 1), 1000);
      return null;
    }
    setUserEmailAcquired(false);
    return false;
  };

  return {
    shopAttributes,
    setShopAttributes,
    currency,
    region,
    klaviyoList,
    paymentProvider,
    paymentProviderSplit,
    frontEndApi,
    currentPath,
    userEmailAcquired,
    setUserEmailAcquired,
    resolveUserEmailAcquired,
  };
});
